import { WarrantyClaimStatus } from '@api/types';
import { TextCodes } from '@api/types/translation/TextCodes';
import { CustomTextField, useWarrantyClaimContext } from '@components';
import { useAuth } from '@hooks';
import { useSerialNumber } from '@hooks/serial-number/serial-number.hooks';
import { Cancel, CheckCircle } from '@mui/icons-material';
import { CircularProgress, InputAdornment, SxProps } from '@mui/material';
import { colors } from '@themes/colors';
import { t } from '@translations';
import debounce from 'lodash/debounce';
import { useEffect, useRef, useState } from 'react';

type WarrantySerialNumberProps = {
  placeholder?: string;
  shouldValidateSerial?: boolean;
  label?: string;
  sx?: SxProps;
  errorText?: string;
};

export const WarrantySerialNumber = ({
  placeholder,
  shouldValidateSerial = false,
  label,
  sx,
  errorText,
}: WarrantySerialNumberProps) => {
  const { form, warrantyData, setSerialNoValid, isReadOnly } = useWarrantyClaimContext();
  const { verifySerialNoRequest, sanitizeSerialNo, verifySerialNoResponse } = useSerialNumber();
  const { isBeremaUser } = useAuth();
  const controller = useRef<AbortController | null>(null);
  const [status, setStatus] = useState<string | null>(null);

  const debouncedCheckSerialNumber = useRef(
    debounce(async (value: string) => {
      if (controller.current) {
        controller.current.abort();
      }
      controller.current = new AbortController();
      form.clearErrors('serialNo');
      if (!value) {
        setSerialNoValid(null);
        setStatus(null);
        return;
      }
      const sanitizedValue = sanitizeSerialNo(value);
      setSerialNoValid(null);
      setStatus('pending');

      try {
        const warrantyId = warrantyData?.id ? warrantyData.id : '';
        const res = await fetch(
          `/api/serial-number/verify?serialNumber=${sanitizedValue}&id=${warrantyId}`,
          {
            signal: controller.current.signal,
          }
        );
        const data = await res.json();
        if (!res.ok) {
          throw new Error('Network response was not ok');
        }
        setSerialNoValid(true);
        setStatus('fulfilled');

        if (!warrantyData && data?.data?.isClaimed && isBeremaUser) {
          form.setError('serialNo', {
            type: 'manual',
            message: t(TextCodes.Warranty.Block.SerialNumberClaimed),
          });
          return;
        }
        if (
          (warrantyData?.status === 'New' || warrantyData?.status === 'Returned') &&
          data?.data?.isClaimed &&
          isBeremaUser
        ) {
          form.setError('serialNo', {
            type: 'manual',
            message: t(TextCodes.Warranty.Block.SerialNumberClaimed),
          });
        }
      } catch (error) {
        if (error instanceof Error) {
          if (error.name === 'AbortError') {
            setSerialNoValid(null);
            return;
          }
          if (errorText) {
            form.setError('serialNo', {
              type: 'manual',
              message: errorText,
            });
            setSerialNoValid(false);
            setStatus('rejected');
          }
        }
      }
    }, 600)
  ).current;

  const getIcon = (status: string | null) => {
    switch (status) {
      case 'pending':
        return <CircularProgress size={20} sx={{ mr: 1 }} />;
      case 'fulfilled':
        return <CheckCircle sx={{ color: colors.primary.complimentaryGreen, mr: 1 }} />;
      case 'rejected':
        return <Cancel sx={{ color: colors.error.main, mr: 1 }} />;
      default:
        return null;
    }
  };
  useEffect(() => {
    if (
      warrantyData &&
      (warrantyData?.status === WarrantyClaimStatus.New ||
        warrantyData?.status === WarrantyClaimStatus.Returned)
    ) {
      verifySerialNoRequest({
        serialNumber: warrantyData.serialNo || '',
        id: warrantyData.id?.toString() || '',
      });
    }
  }, [form, verifySerialNoRequest, warrantyData]);

  useEffect(() => {
    return () => {
      debouncedCheckSerialNumber.cancel();
    };
  }, [debouncedCheckSerialNumber]);

  useEffect(() => {
    const savedFormData = localStorage.getItem('warrantyFormData');
    const parsedData = JSON.parse(savedFormData || '{}');

    if (savedFormData && !warrantyData) {
      form.reset(parsedData);
      verifySerialNoRequest({
        serialNumber: parsedData.serialNo || '',
        id: parsedData.id?.toString() || '',
      });
    }
  }, [form, warrantyData, verifySerialNoRequest]);

  useEffect(() => {
    if (verifySerialNoResponse.isError) {
      setStatus('rejected');
    }

    if (verifySerialNoResponse.isLoading) {
      setStatus('pending');
    }

    if (verifySerialNoResponse.isSuccess) {
      setStatus('fulfilled');
    }
  }, [verifySerialNoResponse]);

  useEffect(() => {
    if (
      verifySerialNoResponse.isSuccess &&
      verifySerialNoResponse?.currentData?.data?.isClaimed &&
      isBeremaUser &&
      warrantyData &&
      (warrantyData?.status === 'New' || warrantyData?.status === 'Returned')
    ) {
      form.setError('serialNo', {
        type: 'manual',
        message: t(TextCodes.Warranty.Block.SerialNumberClaimed),
      });
    }
  }, [verifySerialNoResponse, form, warrantyData, isBeremaUser]);

  return (
    <CustomTextField
      sx={sx}
      variant='outlined'
      margin='normal'
      label={label}
      placeholder={placeholder}
      error={!!form.formState.errors.serialNo}
      helperText={form.formState.errors.serialNo?.message}
      {...form.register('serialNo', {
        required: form.watch('brand') === 'Honda' || form.watch('brand') === 'Cramer',
        onChange: (e) => {
          form.setValue('serialNo', e.target.value);
          debouncedCheckSerialNumber(e.target.value);
        },
      })}
      InputProps={{
        endAdornment: (
          <InputAdornment position='end'>
            {!shouldValidateSerial ? null : getIcon(status)}
          </InputAdornment>
        ),
        readOnly: isReadOnly,
      }}
    />
  );
};
